import React, { useEffect, useRef, type PropsWithChildren } from 'react'
import { createPortal } from 'react-dom'

import { Icon } from '@components/Icon'
import { useIsClient } from '@/hooks/useIsClient'
import type { ReactDialogProps } from '@/types/components/dialog'

import './Dialog.scss'

export type Props = ReactDialogProps

export const Dialog: React.FC<PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
  theme = 'theme-white',
  children,
  className,
}) => {
  const ref = useRef<HTMLDialogElement>(null)

  const isClient = useIsClient()

  const openDialog = () => {
    ref.current?.showModal()
  }

  const closeDialog = () => {
    const dialog = ref.current
    if (!dialog) return

    dialog?.classList.add('dialog--close')

    const handleAnimationEnd = (event: AnimationEvent) => {
      if (event.animationName !== 'dialog-close') return

      dialog.classList.remove('dialog--close')
      dialog.close()
      dialog.removeEventListener('animationend', handleAnimationEnd)
    }

    dialog.addEventListener('animationend', handleAnimationEnd)
  }

  useEffect(() => {
    if (isOpen) {
      openDialog()
    } else {
      closeDialog()
    }
  }, [isOpen])

  const handleInnerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const handleCloseButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    onClose()
  }

  const classes = [theme, 'react-dialog', className]

  const dialogContent = (
    <dialog
      className={classes.join(' ')}
      ref={ref}
      onClick={onClose}
      onCancel={onClose}
    >
      <div className="dialog__inner" onClick={handleInnerClick}>
        <button className="dialog__close" onClick={handleCloseButtonClick}>
          <Icon name="cross" />
        </button>
        {children}
      </div>
    </dialog>
  )

  return isClient ? createPortal(dialogContent, document.body) : null
}
